import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, X, Send, MessageCircle, Phone, Bookmark, BookmarkCheck, Share2, Copy, Download, Loader2, Eye, View, MapPin, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { toast } from "sonner";
import { L, tr } from "@/lib/i18n";
import { statusPill, waLink } from "./statusUtil";
import { isShortlisted, toggleShortlist } from "@/lib/shortlist";
import { buildUnitUrl, whatsappShareLink, copyToClipboard, buildPdfUrl } from "@/lib/share";
import { visibleUnitFields, formatFieldValue, fieldLabel } from "@/lib/unitFinder";
import PaymentPlanSection from "./PaymentPlanSection";
import PlanTransitionOverlay from "./PlanTransitionOverlay";
import PanoramaScene from "./PanoramaScene";

/**
 * UnitDetailPanel — full-screen inspector for a single inventory unit.
 *
 * Sales toolkit additions (Phase A):
 *   - Add to / Remove from Shortlist
 *   - Share menu (copy link · WhatsApp share)
 *   - Download Unit PDF Offer
 *   - Quick jump to Shortlist when items are queued
 */
export default function UnitDetail({
  unit, group, project,
  onClose, onBackToList, onLead, onOpenShortlist, onLocate, trackEvent,
  lang, defaultLang, slug, shortlistCount = 0,
  onSelectPaymentPlan, onTabOpen,
  initialTab,
  unitPanoramas = [], panoramaHotspots = [], fieldDefinitions = [], onPanoramaHotspot, backLabel,
}) {
  // Phase C Feature 12 — the unit may carry a cinematic Scene-to-Plan
  // transition video. When present, it plays before the static plan
  // appears (and only once per unit-open). We also track which tabs
  // exist so the overlay only auto-routes to `plan` if there's one.
  const hasPlan = !!unit?.plan_url;
  const hasGallery = (unit?.gallery_urls || []).length > 0;
  const hasVideo = !!unit?.video_url;
  const hasView = !!(unit?.view_url || unit?.view_video_url);
  // Phase 1 — Panorama 360 MVP — 360 Tour tab visible when any panorama
  // scene is linked to this unit (panorama_parent_unit_id === unit.id).
  const has360 = (unitPanoramas || []).length > 0;
  const [activePanoIdx, setActivePanoIdx] = useState(0);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(null);
  const selectedPaymentPlanId = selectedPaymentPlan?.unitId === unit?.id ? selectedPaymentPlan.planId : null;
  const [overviewFailed, setOverviewFailed] = useState(false);
  const activePano = has360 ? (unitPanoramas[activePanoIdx] || unitPanoramas[0]) : null;
  const activePanoHotspots = activePano
    ? (panoramaHotspots || []).filter((h) => h.panorama_scene_id === activePano.id)
    : [];
  const initial = initialTab && ["info", "plan", "gallery", "video", "view", "tour360"].includes(initialTab)
    ? initialTab
    : "info";
  const [tab, setTab] = useState(initial);
  const [panelOpen, setPanelOpen] = useState(true);
  // Track whether we've played the plan-transition for the current unit
  // open. Avoid replaying when the user toggles between tabs.
  const planTransitionShownRef = useRef(false);
  const [showPlanTransition, setShowPlanTransition] = useState(false);
  useEffect(() => {
    planTransitionShownRef.current = false;
    setTab(initial);
    setActivePanoIdx(0);
    setOverviewFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit?.id]);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  if (!unit) return null;
  const unitName = tr(unit, "name", lang, defaultLang) || unit.name;
  const overviewUrl = unit.gallery_urls?.[0] || unit.plan_preview_url || unit.plan_url || project.cover_url;
  const desc = tr(unit, "description", lang, defaultLang) || unit.description;
  const viewDesc = tr(unit, "view_description", lang, defaultLang) || unit.view_description;
  const shortlisted = isShortlisted(slug, unit.code);
  const unitUrl = buildUnitUrl(slug, unit.code, lang);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const openPlanTab = () => {
    setTab("plan");
    onTabOpen?.("plan");
    trackEvent("plan_open", { entity_id: unit.id });
    // Play the cinematic Scene-to-Plan transition the first time the
    // plan tab is opened for this unit, if configured.
    if (unit.plan_transition_video_url && !planTransitionShownRef.current) {
      planTransitionShownRef.current = true;
      setShowPlanTransition(true);
    }
  };

  const onShortlist = () => {
    if (!unit.code) return;
    toggleShortlist(slug, unit.code);
    trackEvent("shortlist_" + (shortlisted ? "remove" : "add"), { entity_id: unit.id });
    toast.success(shortlisted ? L(lang, "removed_from_shortlist") : L(lang, "added_to_shortlist"));
  };

  const onCopy = async () => {
    if (!unitUrl) return;
    if (await copyToClipboard(unitUrl)) {
      toast.success(L(lang, "link_copied"));
      trackEvent("share_copy", { entity_id: unit.id });
    }
    setShareOpen(false);
  };

  const onPdf = async () => {
    if (!unit.code) return;
    setPdfBusy(true);
    try {
      const r = await fetch(buildPdfUrl(API_BASE, slug, unit.code, lang, selectedPaymentPlanId), { credentials: "include" });
      if (!r.ok) throw new Error("pdf failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-${unit.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      trackEvent("pdf_download", { kind: "single", entity_id: unit.id });
    } catch {
      toast.error(L(lang, "pdf_failed"));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div
      className={`absolute inset-0 z-40 viewer-shell fade-in grid ${panelOpen ? "grid-rows-[minmax(0,45vh)_1fr] md:grid-rows-1 md:grid-cols-[420px_1fr]" : "grid-cols-1"}`}
      data-testid="unit-detail-panel"
    >
      {panelOpen && (
      <aside className="border-r border-white/10 px-6 md:px-8 py-6 flex flex-col min-h-0 overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between gap-2">
          <button onClick={onBackToList} className="text-white/70 hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1" data-testid="unit-back-to-list">
            <ArrowLeft className="h-3 w-3" /> {backLabel || L(lang, "all_units")}
          </button>
          <div className="flex items-center gap-1">
            {onLocate && (
              <button onClick={() => onLocate(unit)} className="pill-nav text-white text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="unit-detail-locate">
                <MapPin className="h-3 w-3" /> {L(lang, "locate")}
              </button>
            )}
            {shortlistCount > 0 && onOpenShortlist && (
              <button onClick={onOpenShortlist} className="pill-nav text-white text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="unit-open-shortlist">
                <Bookmark className="h-3 w-3 fill-white/20" /> {shortlistCount}
              </button>
            )}
            <button onClick={onClose} className="pill-nav text-white text-xs uppercase tracking-wider px-3 py-1.5 rounded-full" aria-label={L(lang, "close")} data-testid="unit-close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{(group && tr(group, "name", lang, defaultLang)) || group?.name || tr(project, "name", lang, defaultLang) || project.name}</div>
          <div className="font-display text-5xl text-white tracking-tight mt-2" data-testid="unit-code">{unit.code || unitName || L(lang, "unit")}</div>
          {unitName && unitName !== unit.code && <div className="text-white/70 mt-1">{unitName}</div>}
          <div className="mt-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${statusPill(unit.status)}`}>{L(lang, unit.status)}</span></div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-white/80 max-h-[40vh] overflow-auto scrollbar-thin pr-2">
          {visibleUnitFields(unit, fieldDefinitions, "detail").map(({ key, fd }) => (
            <div key={key} className="border-t border-white/10 pt-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{fieldLabel(fd, lang, defaultLang)}</div>
              <div className="text-white">{String(formatFieldValue(unit, fd, lang, defaultLang, project) ?? "")}</div>
            </div>
          ))}
        </div>

        {desc && <div className="mt-6 text-sm text-white/70 leading-relaxed border-t border-white/10 pt-4">{desc}</div>}

        <PaymentPlanSection
          unit={unit}
          project={project}
          lang={lang}
          defaultLang={defaultLang}
          fieldDefinitions={fieldDefinitions}
          onPlanSelected={(planId) => { setSelectedPaymentPlan({ unitId: unit.id, planId }); onSelectPaymentPlan?.(planId); }}
        />

        {/* Sales toolkit row */}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap">
          <button
            onClick={onShortlist}
            disabled={!unit.code}
            data-testid="unit-shortlist-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] uppercase tracking-wider disabled:opacity-40 transition ${shortlisted ? "bg-[#C4A47C] text-zinc-900" : "border border-white/15 text-white hover:bg-white/5"}`}
          >
            {shortlisted ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
            {L(lang, shortlisted ? "in_shortlist" : "add_to_shortlist")}
          </button>
          <div className="relative">
            <button
              onClick={() => setShareOpen((v) => !v)}
              disabled={!unitUrl}
              data-testid="unit-share-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] uppercase tracking-wider border border-white/15 text-white hover:bg-white/5 disabled:opacity-40"
            >
              <Share2 className="h-3.5 w-3.5" /> {L(lang, "share")}
            </button>
            {shareOpen && (
              <div className="absolute z-50 mt-1.5 bg-zinc-950 border border-white/10 rounded-lg shadow-2xl min-w-[200px] overflow-hidden" data-testid="unit-share-menu">
                <button onClick={onCopy} className="w-full text-left px-3 py-2.5 text-xs text-white/90 hover:bg-white/5 inline-flex items-center gap-2" data-testid="unit-share-copy">
                  <Copy className="h-3.5 w-3.5" /> {L(lang, "copy_link")}
                </button>
                <a
                  href={whatsappShareLink(`${unit.code} · ${unitName || ""}`, unitUrl)}
                  target="_blank" rel="noreferrer"
                  onClick={() => { setShareOpen(false); trackEvent("share_whatsapp", { entity_id: unit.id }); }}
                  className="block w-full text-left px-3 py-2.5 text-xs text-white/90 hover:bg-white/5 border-t border-white/5 inline-flex items-center gap-2"
                  data-testid="unit-share-whatsapp"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> {L(lang, "whatsapp")}
                </a>
                <a
                  href={`${API_BASE}/api/public/projects/${slug}/qr?unit=${encodeURIComponent(unit.code)}&lang=${encodeURIComponent(lang || "en")}`}
                  download={`qr-${slug}-${unit.code}.png`}
                  onClick={() => { setShareOpen(false); trackEvent("qr_download", { entity_id: unit.id }); }}
                  className="block w-full text-left px-3 py-2.5 text-xs text-white/90 hover:bg-white/5 border-t border-white/5 inline-flex items-center gap-2"
                  data-testid="unit-share-qr"
                >
                  <Download className="h-3.5 w-3.5" /> {L(lang, "download_qr")}
                </a>
              </div>
            )}
          </div>
          <button
            onClick={onPdf}
            disabled={pdfBusy || !unit.code}
            data-testid="unit-pdf-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] uppercase tracking-wider border border-white/15 text-white hover:bg-white/5 disabled:opacity-50"
          >
            {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {L(lang, "download_pdf")}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {project.contact_config?.lead_form_enabled !== false && (
            <button onClick={onLead} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C4A47C] text-zinc-900 text-xs uppercase tracking-wider hover:bg-[#d1b48d] transition" data-testid="unit-lead-btn">
              <Send className="h-4 w-4" /> {L(lang, "enquire")}
            </button>
          )}
          {project.contact_config?.whatsapp && (
            <a href={waLink(project, unit, lang)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { entity_id: unit.id })}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition" data-testid="unit-whatsapp-btn">
              <MessageCircle className="h-4 w-4" /> {L(lang, "whatsapp")}
            </a>
          )}
          {project.contact_config?.phone && (
            <a href={`tel:${project.contact_config.phone}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition">
              <Phone className="h-4 w-4" /> {L(lang, "call")}
            </a>
          )}
        </div>
      </aside>
      )}

      <div className="relative min-h-0 flex flex-col">
        <div className="flex items-center gap-1 px-4 md:px-6 py-4 border-b border-white/10">
          {/* Collapse/expand the black info panel — it can cover a large part
              of the media area (esp. the 360 tour) on tablets. */}
          <button
            onClick={() => setPanelOpen((v) => !v)}
            data-testid="unit-toggle-info-panel"
            title={L(lang, panelOpen ? "hide_info" : "show_info")}
            aria-label={L(lang, panelOpen ? "hide_info" : "show_info")}
            className="mr-1 p-2 rounded-full border border-white/15 text-white/80 hover:text-white hover:bg-white/5 transition shrink-0"
          >
            {panelOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
          </button>
          {hasPlan && <TabBtn active={tab === "plan"} onClick={openPlanTab} testid="tab-plan">{L(lang, "floor_plan")}</TabBtn>}
          {hasGallery && <TabBtn active={tab === "gallery"} onClick={() => { setTab("gallery"); onTabOpen?.("gallery"); trackEvent("gallery_open", { entity_id: unit.id }); }} testid="tab-gallery">{L(lang, "gallery")}</TabBtn>}
          {hasVideo && <TabBtn active={tab === "video"} onClick={() => { setTab("video"); onTabOpen?.("video"); trackEvent("video_open", { entity_id: unit.id }); }} testid="tab-video">{L(lang, "video")}</TabBtn>}
          {hasView && <TabBtn active={tab === "view"} onClick={() => { setTab("view"); onTabOpen?.("view"); trackEvent("view_open", { entity_id: unit.id }); }} testid="tab-view"><Eye className="h-3 w-3 inline mr-1" />{L(lang, "view_from_unit")}</TabBtn>}
          {has360 && <TabBtn active={tab === "tour360"} onClick={() => { setTab("tour360"); onTabOpen?.("tour360"); trackEvent("unit_360_open", { entity_id: unit.id }); }} testid="tab-tour360"><View className="h-3 w-3 inline mr-1" />{L(lang, "tour_360")}</TabBtn>}
          <TabBtn active={tab === "info"} onClick={() => setTab("info")} testid="tab-info">{L(lang, "overview")}</TabBtn>
          {!panelOpen && (
            <button
              onClick={onClose}
              className="ml-auto pill-nav text-white text-xs uppercase tracking-wider px-3 py-1.5 rounded-full shrink-0"
              aria-label={L(lang, "close")} data-testid="unit-close-compact"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden relative">
          {tab === "info" && (
            !overviewFailed && overviewUrl ? <img src={overviewUrl} alt="" decoding="async" onError={() => setOverviewFailed(true)} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center p-6 text-white/55" data-testid="unit-no-media">{L(lang, "media_unavailable")}</div>
          )}
          {tab === "plan" && hasPlan && (
            <img src={unit.plan_url} alt="" decoding="async" className="w-full h-full object-contain bg-black" />
          )}
          {tab === "gallery" && hasGallery && (
            <div className="w-full h-full overflow-auto scrollbar-thin grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
              {unit.gallery_urls.map((u, i) => (
                <img key={i} src={u} alt="" loading="lazy" decoding="async" className="w-full h-72 object-cover rounded" />
              ))}
            </div>
          )}
          {tab === "video" && hasVideo && (
            <video src={unit.video_url} controls className="w-full h-full object-contain bg-black" />
          )}
          {tab === "view" && hasView && (
            <div className="w-full h-full bg-black relative" data-testid="unit-view-panel">
              {unit.view_video_url ? (
                <video src={unit.view_video_url} controls autoPlay muted loop playsInline className="w-full h-full object-cover" data-testid="unit-view-video" />
              ) : (
                <img src={unit.view_url} alt="" className="w-full h-full object-cover" data-testid="unit-view-image" />
              )}
              {(unit.view_direction || viewDesc) && (
                <div className="absolute bottom-6 left-6 right-6 glass-panel rounded-xl px-4 py-3 text-white text-sm pointer-events-none">
                  {unit.view_direction && (
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{unit.view_direction}</div>
                  )}
                  {viewDesc && <div className="mt-1">{viewDesc}</div>}
                </div>
              )}
            </div>
          )}

          {tab === "tour360" && has360 && activePano && (
            <div className="w-full h-full bg-black relative" data-testid="unit-360-panel">
              <PanoramaScene
                scene={activePano}
                hotspots={activePanoHotspots}
                lang={lang}
                defaultLang={defaultLang}
                rtl={lang === "ar"}
                onHotspot={(h) => {
                  trackEvent("panorama_hotspot_click", { entity_id: h.target_id, metadata: { hotspot_id: h.id, action_type: h.action_type, source: "unit_360" } });
                  if (h.action_type === "go_to_panorama" && h.target_id) {
                    // Jump to another unit panorama if it's in this same tour.
                    const idx = unitPanoramas.findIndex((p) => p.id === h.target_id);
                    if (idx >= 0) { setActivePanoIdx(idx); return; }
                  }
                  onPanoramaHotspot?.(h);
                }}
              />
              {unitPanoramas.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 glass-panel rounded-full px-2 py-1.5 flex gap-1 pointer-events-auto" data-testid="unit-360-room-selector">
                  {unitPanoramas.map((p, i) => (
                    <button
                      key={p.id}
                      onClick={() => setActivePanoIdx(i)}
                      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition ${i === activePanoIdx ? "bg-white/15 text-white" : "text-white/65 hover:text-white hover:bg-white/5"}`}
                      data-testid={`unit-360-room-${i}`}
                    >
                      {tr(p, "name", lang, defaultLang) || p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Phase C Feature 12 — cinematic Scene-to-Plan transition */}
          {showPlanTransition && unit.plan_transition_video_url && (
            <PlanTransitionOverlay
              url={unit.plan_transition_video_url}
              onComplete={() => setShowPlanTransition(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition ${active ? "bg-white text-zinc-900" : "text-white/70 hover:bg-white/5"}`}>
      {children}
    </button>
  );
}
